import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MapPin,
  Plane,
  Trash2,
  RotateCcw,
  CalendarClock,
} from "lucide-react";
import { apiJson } from "../lib/queryClient";
import type { FlightSession, Drone } from "../lib/planning/types";
import {
  TRASH_RETENTION_DAYS,
  isExpiredSession,
  daysLeftFor,
  byCreatedDesc,
} from "../lib/briefingSorting";

const PHASE_LABEL: Record<string, string> = {
  planning: "Planung",
  preflight: "Pre-Check",
  hover: "Flight-Check",
  postlanding: "Post-Check",
  debriefing: "Debriefing",
  completed: "Abgeschlossen",
};

export default function Briefings() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: sessions = [], isLoading } = useQuery<FlightSession[]>({
    queryKey: ["/api/sessions"],
  });
  const { data: trashed = [] } = useQuery<FlightSession[]>({
    queryKey: ["/api/sessions", "trashed"],
    queryFn: async () => apiJson<FlightSession[]>("GET", "/api/sessions?trashed=1"),
  });
  const { data: drones = [] } = useQuery<Drone[]>({ queryKey: ["/api/drones"] });

  const droneMap = Object.fromEntries(drones.map((d) => [d.id, d.name]));

  const [expiredOpen, setExpiredOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);

  const { active, expired } = useMemo(() => {
    const a: FlightSession[] = [];
    const e: FlightSession[] = [];
    for (const s of sessions) {
      if (isExpiredSession(s)) e.push(s);
      else a.push(s);
    }
    a.sort(byCreatedDesc);
    e.sort(byCreatedDesc);
    return { active: a, expired: e };
  }, [sessions]);

  const sortedTrash = useMemo(() => [...trashed].sort(byCreatedDesc), [trashed]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["/api/sessions"] });
  }

  const trashMutation = useMutation({
    mutationFn: (id: string) =>
      apiJson("PUT", `/api/sessions/${id}`, { deletedAt: new Date().toISOString() }),
    onSuccess: invalidate,
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => apiJson("PUT", `/api/sessions/${id}`, { deletedAt: null }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiJson("DELETE", `/api/sessions/${id}`),
    onSuccess: invalidate,
  });

  const isEmpty =
    active.length === 0 && expired.length === 0 && sortedTrash.length === 0;

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="text-center text-[var(--color-app-text-secondary)] py-12">Lädt …</div>
      ) : isEmpty ? (
        <div className="card text-center py-10">
          <div
            className="mb-4 mx-auto grid place-items-center text-white"
            style={{
              width: 64,
              height: 64,
              borderRadius: 18,
              background: "linear-gradient(160deg, var(--color-brand-400) 0%, var(--color-brand-600) 100%)",
              boxShadow: "0 12px 28px rgba(10, 77, 140, 0.32)",
            }}
          >
            <Plane size={28} />
          </div>
          <p className="text-[15px] text-[var(--color-app-text-secondary)]">
            Noch keine Briefings vorhanden.
          </p>
          <Link to="/flugplanung" className="btn-primary inline-flex mt-4">
            Neue Planung starten
          </Link>
        </div>
      ) : (
        <>
          {active.length > 0 ? (
            <ul className="ios-list" data-testid="list-briefings">
              {active.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/briefings/${s.id}`}
                    className="ios-row"
                    data-testid={`row-briefing-${s.id}`}
                  >
                    <span className="ios-row-icon">
                      <MapPin size={18} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-semibold text-[var(--color-app-text)] truncate">
                        {s.locationName || `${s.latitude.toFixed(3)}, ${s.longitude.toFixed(3)}`}
                      </span>
                      <span className="block text-[12.5px] text-[var(--color-app-text-secondary)] mt-0.5 truncate">
                        {droneMap[s.droneId] || s.droneId} · {new Date(s.createdAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })} · {PHASE_LABEL[s.currentPhase] || s.currentPhase}
                      </span>
                    </span>
                    <StatusBadge status={s.status} />
                    <ChevronRight size={18} className="text-[var(--color-app-text-tertiary)] flex-shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="card text-center py-8 text-sm text-[var(--color-app-text-secondary)]">
              Keine aktiven Briefings.
            </div>
          )}

          {expired.length > 0 ? (
            <section className="space-y-2">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)]"
                onClick={() => setExpiredOpen((o) => !o)}
                data-testid="button-toggle-expired"
              >
                <span className="text-[13px] font-semibold text-[var(--color-app-text-secondary)]">
                  Abgelaufene Briefings ({expired.length})
                </span>
                {expiredOpen ? (
                  <ChevronUp size={16} className="text-[var(--color-app-text-tertiary)]" />
                ) : (
                  <ChevronDown size={16} className="text-[var(--color-app-text-tertiary)]" />
                )}
              </button>
              {expiredOpen ? (
                <ul className="ios-list" data-testid="list-expired">
                  {expired.map((s) => (
                    <li key={s.id} className="ios-row !cursor-default flex-wrap gap-y-2">
                      <span className="ios-row-icon">
                        <MapPin size={18} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <Link
                          to={`/briefings/${s.id}`}
                          className="block text-[15px] font-semibold text-[var(--color-app-text)] truncate hover:underline"
                          data-testid={`row-expired-${s.id}`}
                        >
                          {s.locationName || `${s.latitude.toFixed(3)}, ${s.longitude.toFixed(3)}`}
                        </Link>
                        <span className="block text-[12.5px] text-[var(--color-app-text-secondary)] mt-0.5 truncate">
                          {droneMap[s.droneId] || s.droneId}
                          {s.planning?.plannedDate
                            ? ` · geplant ${formatPlanned(s.planning.plannedDate, s.planning.plannedTime)}`
                            : ""}
                        </span>
                      </span>
                      <div className="flex items-center gap-2 ml-auto">
                        <button
                          type="button"
                          className="btn-secondary text-xs px-2.5 py-1 inline-flex items-center gap-1"
                          onClick={() => navigate(`/flugplanung/${s.id}`)}
                          data-testid={`button-replan-${s.id}`}
                        >
                          <CalendarClock size={14} /> Neu planen
                        </button>
                        <button
                          type="button"
                          className="btn-secondary text-xs px-2.5 py-1 inline-flex items-center gap-1"
                          onClick={() => trashMutation.mutate(s.id)}
                          disabled={trashMutation.isPending}
                          data-testid={`button-trash-${s.id}`}
                        >
                          <Trash2 size={14} /> Papierkorb
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}

          {sortedTrash.length > 0 ? (
            <section className="space-y-2">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)]"
                onClick={() => setTrashOpen((o) => !o)}
                data-testid="button-toggle-trash"
              >
                <span className="text-[13px] font-semibold text-[var(--color-app-text-secondary)]">
                  Papierkorb ({sortedTrash.length}) — {TRASH_RETENTION_DAYS} Tage Aufbewahrung
                </span>
                {trashOpen ? (
                  <ChevronUp size={16} className="text-[var(--color-app-text-tertiary)]" />
                ) : (
                  <ChevronDown size={16} className="text-[var(--color-app-text-tertiary)]" />
                )}
              </button>
              {trashOpen ? (
                <ul className="ios-list" data-testid="list-trash">
                  {sortedTrash.map((s) => {
                    const daysLeft = daysLeftFor(s.deletedAt);
                    return (
                      <li key={s.id} className="ios-row !cursor-default flex-wrap gap-y-2">
                        <span className="ios-row-icon">
                          <Trash2 size={18} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[15px] font-semibold text-[var(--color-app-text)] truncate">
                            {s.locationName || `${s.latitude.toFixed(3)}, ${s.longitude.toFixed(3)}`}
                          </span>
                          <span className="block text-[12.5px] text-[var(--color-app-text-secondary)] mt-0.5 truncate">
                            {droneMap[s.droneId] || s.droneId} · noch {daysLeft} Tag{daysLeft === 1 ? "" : "e"} bis zur Löschung
                          </span>
                        </span>
                        <div className="flex items-center gap-2 ml-auto">
                          <button
                            type="button"
                            className="btn-secondary text-xs px-2.5 py-1 inline-flex items-center gap-1"
                            onClick={() => restoreMutation.mutate(s.id)}
                            disabled={restoreMutation.isPending}
                            data-testid={`button-restore-${s.id}`}
                          >
                            <RotateCcw size={14} /> Wiederherstellen
                          </button>
                          <button
                            type="button"
                            className="btn-danger text-xs px-2.5 py-1 inline-flex items-center gap-1"
                            onClick={() => {
                              if (confirm("Dieses Briefing endgültig löschen? Das kann nicht rückgängig gemacht werden.")) {
                                deleteMutation.mutate(s.id);
                              }
                            }}
                            disabled={deleteMutation.isPending}
                            data-testid={`button-delete-${s.id}`}
                          >
                            <Trash2 size={14} /> Endgültig löschen
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function formatPlanned(date: string, time?: string): string {
  const d = new Date(`${date}T${time || "00:00"}:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleString("de-DE", {
    dateStyle: "short",
    ...(time ? { timeStyle: "short" } : {}),
  });
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "go" ? "pill-go" : status === "caution" ? "pill-caution" : status === "nogo" ? "pill-nogo" : "ios-pill bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-secondary)]";
  return <span className={cls}>{status}</span>;
}
